"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InputNumber = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const Iconista_1 = require("../../icons/Iconista");
const Input_1 = require("../Input");
const BasicButton_1 = require("../BasicButton");
const DragSlider_1 = require("../../1-inline/DragSlider");
const DragSliderHandle_1 = require("../../1-inline/DragSlider/DragSliderHandle");
const blockClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    gap: '4px',
    vAlign: 'middle',
});
const inputWrapClass = (0, nano_theme_1.rule)({
    flex: '1 1 auto',
    minWidth: 0,
    'input[type=number]': {
        '-moz-appearance': 'textfield',
    },
    'input[type=number]::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        mar: 0,
    },
    'input[type=number]::-webkit-outer-spin-button': {
        '-webkit-appearance': 'none',
        mar: 0,
    },
});
const clamp = (n, min, max) => {
    if (min !== undefined && n < min)
        return min;
    if (max !== undefined && n > max)
        return max;
    return n;
};
const decimalsOf = (step) => {
    if (!Number.isFinite(step))
        return 0;
    const s = String(step);
    const dot = s.indexOf('.');
    return dot < 0 ? 0 : s.length - dot - 1;
};
const round = (n, decimals) => {
    if (decimals <= 0)
        return Math.round(n);
    const factor = 10 ** decimals;
    return Math.round(n * factor) / factor;
};
const InputNumber = ({ value, onChange, onChangeEnd, min, max, step = 1, decimals: decimalsProp, disabled, drag, dragAxis, dragSensitivity, dragHandle, dragHideStartDot, dragStartDotSize, className, style, inputProps, buttonProps, }) => {
    const decimals = decimalsProp ?? decimalsOf(step);
    const numeric = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    const [text, setText] = React.useState(value === undefined ? '' : String(numeric));
    const lastEmittedRef = React.useRef(value);
    React.useEffect(() => {
        if (value === lastEmittedRef.current)
            return;
        lastEmittedRef.current = value;
        setText(value === undefined ? '' : String(value));
    }, [value]);
    const emit = (next) => {
        const clamped = round(clamp(next, min, max), decimals);
        lastEmittedRef.current = clamped;
        setText(String(clamped));
        onChange?.(clamped);
        return clamped;
    };
    const commit = (next) => {
        const clamped = emit(next);
        onChangeEnd?.(clamped);
        return clamped;
    };
    const dec = () => commit(numeric - step);
    const inc = () => commit(numeric + step);
    const handleChange = (raw) => {
        setText(raw);
        if (raw === '' || raw === '-' || raw === '.')
            return;
        const parsed = Number(raw);
        if (!Number.isFinite(parsed))
            return;
        const clamped = round(clamp(parsed, min, max), decimals);
        lastEmittedRef.current = clamped;
        onChange?.(clamped);
    };
    const handleBlur = (e) => {
        let final;
        if (text === '' || !Number.isFinite(Number(text))) {
            final = round(clamp(numeric, min, max), decimals);
        }
        else {
            final = round(clamp(Number(text), min, max), decimals);
        }
        setText(String(final));
        lastEmittedRef.current = final;
        onChangeEnd?.(final);
        inputProps?.onBlur?.(e);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            inc();
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            dec();
        }
        else {
            inputProps?.onKeyDown?.(e);
        }
    };
    const atMin = min !== undefined && numeric <= min;
    const atMax = max !== undefined && numeric >= max;
    const defaultSensitivity = typeof min === 'number' && typeof max === 'number' ? Math.max(step, (max - min) / 200) : step;
    const sensitivity = dragSensitivity ?? defaultSensitivity;
    const formatDrag = React.useCallback((v) => {
        const clamped = round(clamp(v, min, max), decimals);
        return decimals > 0 ? clamped.toFixed(decimals) : String(clamped);
    }, [min, max, decimals]);
    // Start a new drag from whatever the input currently shows — handles the
    // case where the consumer subscribed only to `onChangeEnd` (so `value` is
    // still stale from the previous commit) but the input already displays a
    // newer typed/dragged value.
    const numericLive = React.useMemo(() => {
        const parsed = Number(text);
        return Number.isFinite(parsed) ? parsed : numeric;
    }, [text, numeric]);
    const handleDragEnd = React.useCallback((final, cancelled) => {
        if (cancelled) {
            // Restore the display to the consumer's committed value.
            const restore = round(clamp(numeric, min, max), decimals);
            setText(String(restore));
            lastEmittedRef.current = restore;
            onChange?.(restore);
        }
        else {
            const rounded = round(clamp(final, min, max), decimals);
            setText(String(rounded));
            lastEmittedRef.current = rounded;
            onChangeEnd?.(rounded);
        }
    }, [numeric, min, max, decimals, onChange, onChangeEnd]);
    const dragRight = drag ? (React.createElement(DragSlider_1.DragSlider, { value: numericLive, onChange: emit, onEnd: handleDragEnd, min: min, max: max, step: step, sensitivity: sensitivity, axis: dragAxis, format: formatDrag, disabled: disabled, hideStartDot: dragHideStartDot, startDotSize: dragStartDotSize, style: { marginRight: -4 } }, dragHandle ?? React.createElement(DragSliderHandle_1.DragSliderHandle, { disabled: disabled }))) : (inputProps?.right);
    return (React.createElement("span", { className: blockClass + (className ? ' ' + className : ''), style: style },
        React.createElement(BasicButton_1.BasicButton, { ...buttonProps, disabled: disabled || atMin, title: "Decrease", onClick: dec },
            React.createElement(Iconista_1.Iconista, { set: "tabler", icon: "minus", width: 16, height: 16 })),
        React.createElement("span", { className: inputWrapClass },
            React.createElement(Input_1.Input, { align: "center", ...inputProps, type: "number", value: text, disabled: disabled, onChange: handleChange, onBlur: handleBlur, onKeyDown: handleKeyDown, right: dragRight })),
        React.createElement(BasicButton_1.BasicButton, { ...buttonProps, disabled: disabled || atMax, title: "Increase", onClick: inc },
            React.createElement(Iconista_1.Iconista, { set: "tabler", icon: "plus", width: 16, height: 16 }))));
};
exports.InputNumber = InputNumber;
